// Plan 08-05 — MatchWsClient: WebSocket Hibernation client with reconnect FSM.
// Plan 21-06 (Phase 21 / INFRA-05) — bearer.* subprotocol replaced by ticket.*
// (the WS upgrade no longer carries the long-lived bearer JWT; a 60s single-
// use opaque ticket is minted by POST /api/match/{matchId}/ws-ticket before
// each (re)connect — CR-04 resolved at the protocol layer).
//
// Architecture:
//   - URL builder NEVER injects `?slot=` (NY-08-10 defense-in-depth — slot
//     identity is enforced server-side via D1 lookup and X-Player-Slot trusted
//     header; the URL has no business carrying it).
//   - Bearer source: a `getBearer: () => string | null` callback supplied at
//     construction. The bearer is read FRESH on every (re)connect attempt
//     so silent-refresh + post-logout reconnects pick up the latest JWT
//     (or `null` → terminate). Plan 09-10 CR-03 fix replaced an immutable
//     captured `_bearer: string` field — that captured-at-construction
//     pattern would (a) reconnect with a stale JWT after silent-refresh
//     rotated the token and (b) reconnect as a just-logged-out user when
//     the auth atom had been cleared between (re)connects.
//   - Subprotocol = ['runegambit.v1', `ticket.${ticket}`] (Plan 21-06).
//     `open()` is async — it awaits `fetchWsTicket(matchId, getBearer)` to
//     mint a fresh 60s single-use ticket before constructing the WebSocket.
//     Pitfall 6: backend echoes ONLY 'runegambit.v1' in the upgrade response
//     (the ticket.* token is consumed and stripped server-side).
//   - Frame format: [u8 schema_version=1][protobuf bytes] decoded via Plan 04
//     codec (`decodeFrame` / `decodeStateSnapshot`).
//   - On STATE_SNAPSHOT → onSnapshot(playerView).
//   - On PING → respond with PONG carrying the same `ts` (RFC ping/pong
//     keepalive — server uses setWebSocketAutoResponse for transport pings,
//     application-level PING/PONG envelopes are also defined).
//   - On 1003 / 1008 close codes (protocol violation / unsupported schema) →
//     onError, no auto-reconnect (the client cannot recover from a bad
//     wire-version or malformed frame stream).
//   - On other close codes (1006 / 4xxx) → reconnect at `[1, 2, 4, 8, 16]s`
//     with ±20% jitter. After 3 failed reconnects → onFallback() fires and
//     the FSM transitions to 'fallback' / 'terminated'.
//
// State diagram:
//
//   disconnected ──open()──> connecting ──ws.onopen──> connected
//                                │                          │
//                                │                ws.onclose(non-1000) │
//                                │                          ▼
//                                │                  reconnecting
//                                │                          │
//                                │              [3 failures] │
//                                │                          ▼
//                                │                     fallback
//                                │                          │
//                                │                          ▼
//                                ▼                    terminated
//                         (1003/1008 close)
//
// onFallback() is the seam where Plan 05's `useMatchData` hook starts the
// ETag polling fallback (`startPolling` from `./polling-fallback`).

import type { PlayerView } from '../game/PlayerViewSchema'
import {
  B12FirewallViolation,
  decodeFrame,
  decodeStateSnapshot,
  encodeFrame,
  ProtocolError,
  SCHEMA_VERSION,
  SchemaVersionError,
} from '../game/proto/codec'
import { runegambit } from '../game/proto/runegambit.pb.js'
import { $affinityReveal, type AffinityReveal } from '../stores/affinityReveal'
import { $scoreReveal, type ScoreReveal } from '../stores/scoreReveal'
import { fetchWsTicket } from './ws-ticket'

// Module-scope test helper accessor — exposes the rate-limit reset for
// vitest fake-timers paths. Declared near the top so __test__ binding
// below can pick it up.

export type WsClientState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'fallback'
  | 'terminated'

export interface MatchWsCallbacks {
  /** Fires on STATE_SNAPSHOT decoded PlayerView. */
  onSnapshot: (view: PlayerView) => void
  /** Fires on protocol-fatal close codes (1003 / 1008) or onError event. */
  onError: (err: Error) => void
  /** Fires when 3 reconnect attempts have failed; caller starts polling. */
  onFallback: () => void
  /** Optional: fires on initial ws.onopen — caller flushes outbound queue. */
  onConnected?: () => void
  /**
   * Plan 14-08 (MUI-09) — fires whenever a STATE_SNAPSHOT or TURN_APPLIED
   * envelope carries a `status` string on the PlayerView wrapper. The
   * envelope-level `status` is sourced from `MatchInternalState.status` on
   * the backend (apps/backend/src/match/ws/protocol.ts). Terminal values on
   * the wire are 'completed' | 'abandoned' | 'timeout_resolved' |
   * 'stalemate_resolved'. The true per-player outcome (win/lose/draw) is
   * derived from `PlayerView.winner_slot` — NOT from this status string.
   * The consumer (useMatchData) re-exposes this for MatchScreen terminal
   * detection; MatchScreen's `deriveTerminalOutcome` then derives the outcome.
   */
  onStatus?: (status: string) => void
}

const SUBPROTOCOL_V1 = 'runegambit.v1' as const
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000] as const
const MAX_RECONNECTS = 3

/**
 * Build the Sec-WebSocket-Protocol header array. Plan 21-06: second token
 * is now `ticket.<value>` — a 60s single-use opaque ticket minted by the
 * server (apps/backend/src/auth/ws-ticket.ts; route at POST
 * /api/match/{matchId}/ws-ticket). The ticket is already base64url-safe
 * from the server mint (32 random bytes → 43+ base64url chars), so no
 * client-side encoding step is needed.
 *
 * Replaces the prior `bearer.<base64url(jwt)>` pattern — the bearer JWT
 * no longer travels in the WS upgrade request and no longer lands in
 * Cloudflare edge access logs (CR-04 resolution at the protocol layer).
 */
function buildSubprotocols(ticket: string): [string, string] {
  return [SUBPROTOCOL_V1, `ticket.${ticket}`]
}

/**
 * Compute exp-backoff delay in ms with ±20% jitter.
 * `attempt` is 0-indexed: attempt=0 → BACKOFF_MS[0] = 1000ms.
 */
function backoffMs(attempt: number): number {
  const base = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 16000
  const jitter = base * 0.2
  // Random ±20% in [-jitter, +jitter].
  const delta = Math.random() * 2 * jitter - jitter
  return Math.max(0, base + delta)
}

/**
 * Build the WS URL. NEVER appends a `?slot=` query parameter (NY-08-10
 * defense-in-depth — slot is server-derived from the D1 user→slot lookup).
 */
function buildWsUrl(matchId: string): string {
  const base = (import.meta.env?.VITE_WS_BASE_URL ?? '').replace(/\/+$/, '')
  return `${base}/api/match/${matchId}/ws`
}

export const __test__ = {
  buildSubprotocols,
  backoffMs,
  buildWsUrl,
  // Plan 20-07 — META-08 test helpers.
  resetEmoteRateLimit: (): void => {
    __resetEmoteRateLimit()
  },
}

/**
 * Bearer source callback. MUST return the freshest JWT at the moment of
 * the call, or `null` when the user is logged out / the auth atom is
 * cleared. CR-03 fix: this callback is invoked on EVERY (re)connect — never
 * captured into a constructor field — so silent-refresh + logout transitions
 * propagate to the next ws upgrade.
 *
 * Production wiring lives in `apps/web/src/hooks/useMatchData.ts` and
 * resolves to `() => effectiveBearerJwt()` (ARCA-93: real bearer when
 * authenticated, else the anon practice JWT for guest bot matches).
 */
export type GetBearer = () => string | null

export class AuthClearedError extends Error {
  constructor(message = 'AUTH_CLEARED') {
    super(message)
    this.name = 'AuthClearedError'
  }
}

// ───────────────────────────────────────────────────────────────────────
// Plan 20-07 — META-08 — emote send / receive.
//
// `sendEmote(emoteId)` encodes an EMOTE_SEND envelope (kind=6) carrying a
// non-empty emote_id and writes it to the WS as a versioned frame
// ([u8 schema_version=1][protobuf]). The client enforces an OPTIMISTIC
// 10s rate limit that mirrors the server-authoritative limit (Plan 20-03
// EMOTE_RATE_LIMIT_MS = 10_000); the server remains the source of truth
// (T-20-45 — bypassing the client check is a no-op against server state).
//
// `onEmoteReceived(callback)` registers a listener for EMOTE_RECEIVED
// (kind=7) frames. When the WS receives one, the registered callbacks
// fire with `{emoteId, senderSlot, sentAt}` — all SERVER-stamped fields
// (the client cannot forge senderSlot via the EmoteSend payload).
// Malformed frames silently no-op (T-20-47 mitigation: missing/wrong-
// shape fields → drop, no throw).
//
// Returns an unsubscribe function. Multiple listeners are supported
// (mirrors the React pattern where a component registers on mount and
// unregisters on unmount).
// ───────────────────────────────────────────────────────────────────────

/** Plan 20-03 backend rate-limit constant; client mirrors for optimistic dedup. */
export const EMOTE_CLIENT_RATE_LIMIT_MS = 10_000

export interface EmoteReceivedEvent {
  readonly emoteId: string
  readonly senderSlot: 0 | 1
  readonly sentAt: number
}

const emoteReceivedCallbacks: Array<(e: EmoteReceivedEvent) => void> = []

/**
 * Register a listener for inbound EMOTE_RECEIVED frames. Returns an
 * unsubscribe function. Safe to call before the WS is open — the
 * registry is module-scoped and persists across reconnects.
 */
export function onEmoteReceived(
  callback: (e: EmoteReceivedEvent) => void,
): () => void {
  emoteReceivedCallbacks.push(callback)
  return () => {
    const i = emoteReceivedCallbacks.indexOf(callback)
    if (i >= 0) emoteReceivedCallbacks.splice(i, 1)
  }
}

/**
 * Module-scope state for the optimistic client-side rate limit. Mirrors
 * the slot-keyed DO-storage window the backend keeps at
 * `emote_rate_limit:{slot}` (Plan 20-03; CR-01 fix moved this from
 * per-socket attachment to slot-keyed storage so multi-socket bypass is
 * impossible). Client-side state is reset on page reload — there is no
 * need for cross-session persistence; the server is the authoritative
 * gate.
 */
let lastEmoteSentTs = 0

/**
 * Reset the rate-limit clock. Test helper exported via __test__ — never
 * called by application code. Production rate-limit state lives for the
 * lifetime of the module / page.
 */
function __resetEmoteRateLimit(): void {
  lastEmoteSentTs = 0
}

// Track the singleton WsClient instance so the module-scoped sendEmote
// can find its underlying socket. The MatchScreen registers it on mount.
let activeWsClient: MatchWsClient | null = null

/**
 * Register the active MatchWsClient for emote-send routing. The
 * MatchScreen calls this with its WS client instance so the module-
 * scoped `sendEmote` can write to the right socket.
 */
export function registerActiveWsClient(client: MatchWsClient | null): void {
  activeWsClient = client
}

/**
 * Send an EMOTE_SEND envelope. Client-side optimistic 10s rate-limit
 * mirrors the server's authoritative limit (Plan 20-03). Returns true
 * if the frame was sent; false if rate-limited or no active socket.
 *
 * The server is the source-of-truth — bypassing this client-side check
 * (e.g., via direct WS manipulation) has no effect on server state
 * (T-20-45 disposition: accept; the server silently drops repeats).
 */
export function sendEmote(emoteId: string): boolean {
  if (typeof emoteId !== 'string' || emoteId.length === 0) return false
  const now = Date.now()
  if (now - lastEmoteSentTs < EMOTE_CLIENT_RATE_LIMIT_MS) return false
  if (activeWsClient === null) return false
  const sent = activeWsClient._sendEmoteFrame(emoteId)
  if (sent) lastEmoteSentTs = now
  return sent
}

export class MatchWsClient {
  private readonly _matchId: string
  private readonly _getBearer: GetBearer
  private readonly _cb: MatchWsCallbacks
  private _ws: WebSocket | null = null
  private _state: WsClientState = 'disconnected'
  private _reconnectAttempts = 0
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null
  // MUI-08 (Plan 14-07): race-safe close flag. Set BEFORE any other
  // reconnect-bound state change in close() AND in the natural-end branches
  // of _handleClose (codes 1000 / 1003 / 1008). open() short-circuits on
  // _closed so a stale caller cannot resurrect a terminated client, and the
  // reconnect-timer body checks _closed before invoking this.open() so a
  // race between close() and a scheduled timer cannot spawn a new socket.
  private _closed = false

  /**
   * @param matchId  match UUID for the WS upgrade target
   * @param getBearer callback returning the current bearer JWT (or null when
   *                  unauthenticated). Called on every (re)connect — never
   *                  cached internally. CR-03 fix.
   * @param cb       lifecycle callbacks (onSnapshot/onError/onFallback/...)
   */
  constructor(matchId: string, getBearer: GetBearer, cb: MatchWsCallbacks) {
    this._matchId = matchId
    this._getBearer = getBearer
    this._cb = cb
  }

  get state(): WsClientState {
    return this._state
  }

  /**
   * Open the WebSocket. Plan 21-06: now async — mints a fresh ws-ticket
   * via `fetchWsTicket(matchId, getBearer)` BEFORE constructing the
   * WebSocket. Errors from the ticket fetch (AuthClearedError on null
   * bearer, WsTicketAuthError on 401, etc.) route to `onError` and
   * transition state to 'terminated' — the existing FSM short-circuits
   * apply, so no socket is spawned on auth failure.
   *
   * Returns `Promise<void>` so callers can `await` if they want to gate
   * on connection setup; the reconnect-timer body fires-and-forgets via
   * `void this.open().catch(...)` since the open() body already routes
   * errors through the callback.
   */
  async open(): Promise<void> {
    // MUI-08 (Plan 14-07): a closed client is permanently terminated. Refuse
    // to resurrect — a stale caller (e.g. a React effect after unmount) must
    // not spawn a new socket on an already-closed instance.
    if (this._closed) return
    if (this._state === 'connecting' || this._state === 'connected') return
    // Set 'connecting' BEFORE the async ticket fetch so a concurrent call
    // to open() short-circuits at the state-machine guard above.
    this._state = 'connecting'

    // CR-03 (Plan 09-10) + Plan 21-06: read the bearer FRESH inside
    // fetchWsTicket — the getter is passed through, never cached. A null
    // bearer surfaces as AuthClearedError; an expired/invalid JWT
    // surfaces as WsTicketAuthError. Both terminate the client cleanly
    // (no auto-reconnect — there is no recoverable state to retry from).
    let ticket: string
    try {
      ticket = await fetchWsTicket(this._matchId, this._getBearer)
    } catch (err) {
      this._cb.onError(err instanceof Error ? err : new Error(String(err)))
      this._state = 'terminated'
      return
    }

    // MUI-08 race guard: between the await and here, close() may have set
    // _closed = true. Honor it — do not construct a WebSocket on an
    // explicitly-closed client. Plan 21-06 (T-21-35).
    if (this._closed) return

    const url = buildWsUrl(this._matchId)
    const protos = buildSubprotocols(ticket)
    let ws: WebSocket
    try {
      ws = new WebSocket(url, protos)
    } catch (e) {
      this._cb.onError(e instanceof Error ? e : new Error(String(e)))
      this._state = 'terminated'
      return
    }
    if ('binaryType' in ws) {
      ;(ws as { binaryType: BinaryType }).binaryType = 'arraybuffer'
    }
    this._ws = ws

    ws.onopen = () => this._handleOpen()
    ws.onmessage = (e: MessageEvent) => this._handleMessage(e)
    ws.onclose = (e: CloseEvent) => this._handleClose(e)
    ws.onerror = (_e: Event) => {
      // Browser fires onerror with an opaque Event; the close event that
      // immediately follows carries the actionable code. Surface a synthetic
      // error so callers know the socket is unhealthy.
      this._cb.onError(new Error('ws error event'))
    }
  }

  /**
   * Explicit close — cancels any pending reconnect timer and transitions to
   * 'terminated'. Use code 1000 for clean app-driven shutdown.
   *
   * MUI-08 (Plan 14-07): sets `_closed = true` as the FIRST effect, BEFORE
   * any timer manipulation or socket close. The flag is checked by both
   * `open()` and the reconnect-timer body, so even if a stale timer somehow
   * fires after this method has returned, the socket cannot be resurrected.
   */
  close(code = 1000): void {
    // MUI-08: race-safe close — flag MUST be set before any reconnect-bound
    // state change so the timer body's `if (this._closed) return` guard sees
    // it on the next tick.
    this._closed = true
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
    if (this._ws !== null) {
      const ws = this._ws
      if (import.meta.env.DEV && ws.readyState === WebSocket.CONNECTING) {
        // Dev-only: React Strict Mode's synthetic unmount-then-remount calls
        // cleanup before `onopen` fires, which makes Chrome log
        // "WebSocket is closed before the connection is established" as a
        // benign warning. Silence it by detaching our handlers and deferring
        // the close until the upgrade actually completes. The user-visible
        // effect is identical (the socket ends up closed) but the warning
        // doesn't fire. Strict Mode + this entire code path are dev-only;
        // production builds preserve the original behaviour.
        ws.onopen = null
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        ws.addEventListener(
          'open',
          () => {
            try {
              ws.close(code)
            } catch {
              // ignore
            }
          },
          { once: true },
        )
      } else {
        try {
          ws.close(code)
        } catch {
          // ignore — socket already closing/closed
        }
      }
    }
    this._state = 'terminated'
  }

  private _handleOpen(): void {
    this._state = 'connected'
    this._reconnectAttempts = 0
    this._cb.onConnected?.()
  }

  /**
   * Send bytes through the underlying WebSocket. Wraps the strict-mode
   * ArrayBufferLike vs ArrayBuffer mismatch by passing the underlying buffer
   * — Uint8Array's `buffer` is technically `ArrayBufferLike` (which is a
   * union with SharedArrayBuffer), so we cast through `unknown` to satisfy
   * `ws.send(BufferSource)`. The runtime value is always a plain ArrayBuffer.
   */
  private _sendBytes(bytes: Uint8Array): void {
    if (!this._ws) return
    this._ws.send(bytes as unknown as ArrayBuffer)
  }

  /**
   * Plan 20-07 — META-08 — encode + send an EMOTE_SEND envelope.
   * Internal API consumed by the module-scoped `sendEmote(emoteId)`
   * helper above. Constructs a fresh Envelope (Pitfall 4 — never reuses
   * a singleton) and wraps it with the SCHEMA_VERSION byte. Returns
   * true on success; false if the socket is not yet open. Caller is
   * responsible for the rate-limit gate.
   */
  _sendEmoteFrame(emoteId: string): boolean {
    if (!this._ws || this._state !== 'connected') return false
    try {
      const frame = encodeFrame({
        kind: runegambit.v1.MessageKind.EMOTE_SEND,
        emoteSend: runegambit.v1.EmoteSend.create({ emoteId }),
      })
      this._sendBytes(frame)
      return true
    } catch {
      return false
    }
  }

  private _handleMessage(e: MessageEvent): void {
    const data = e.data
    let bytes: Uint8Array
    if (data instanceof ArrayBuffer) {
      bytes = new Uint8Array(data)
    } else if (data instanceof Uint8Array) {
      bytes = data
    } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
      // Should not happen — we set binaryType='arraybuffer'. Bail.
      this._cb.onError(new Error('unexpected Blob frame'))
      return
    } else {
      this._cb.onError(new Error('unexpected frame type'))
      return
    }

    try {
      const env = decodeFrame(bytes)
      switch (env.kind) {
        case runegambit.v1.MessageKind.STATE_SNAPSHOT: {
          const view = decodeStateSnapshot(bytes)
          if (view !== null) this._cb.onSnapshot(view)
          // Plan 14-08 (MUI-09) — surface envelope-level match status if
          // the callback is registered. `status` lives on the PlayerView
          // wrapper alongside yourStateJson (backend protocol.ts:86).
          const statusSnap = env.stateSnapshot?.yourState?.status
          if (typeof statusSnap === 'string' && this._cb.onStatus) {
            this._cb.onStatus(statusSnap)
          }
          break
        }
        case runegambit.v1.MessageKind.TURN_APPLIED: {
          // TURN_APPLIED also carries `your_state` — decode and surface.
          const stateJson = env.turnApplied?.yourState?.yourStateJson
          if (typeof stateJson === 'string') {
            // Reuse decodeStateSnapshot's parse logic by extracting
            // your_state inline (TURN_APPLIED has the same yourState shape).
            try {
              // Phase 31 Plan 31-04 (RUNE-33): the terminal TURN_APPLIED frame
              // may carry an `_affinity_reveal` sidecar on the JSON-serialised
              // your_state payload (NOT a field on PlayerView — B12 firewall
              // hold). Extract it first, push to the dedicated atom, then
              // strip the key before casting the remainder as PlayerView.
              const raw = JSON.parse(stateJson) as Record<string, unknown>
              const sidecar = raw._affinity_reveal as
                | AffinityReveal
                | null
                | undefined
              if (
                sidecar !== null &&
                sidecar !== undefined &&
                typeof sidecar === 'object' &&
                Array.isArray(sidecar.a) &&
                sidecar.a.length === 4 &&
                Array.isArray(sidecar.b) &&
                sidecar.b.length === 4
              ) {
                $affinityReveal.set(sidecar)
              }
              // ARCA-152 — terminal score+captures sidecar, handled the same
              // way as `_affinity_reveal`: validate the {a,b}.{score,captures}
              // shape, push to the dedicated atom, then strip the key.
              const scoreSidecar = raw._score_reveal as
                | ScoreReveal
                | null
                | undefined
              if (
                scoreSidecar !== null &&
                scoreSidecar !== undefined &&
                typeof scoreSidecar === 'object' &&
                typeof scoreSidecar.a?.score === 'number' &&
                typeof scoreSidecar.a?.captures === 'number' &&
                typeof scoreSidecar.b?.score === 'number' &&
                typeof scoreSidecar.b?.captures === 'number'
              ) {
                $scoreReveal.set(scoreSidecar)
              }
              // Destructure the sidecars OFF the raw object so downstream
              // consumers never see `_affinity_reveal` / `_score_reveal` on the
              // PlayerView shape (defense-in-depth on the type-construction
              // firewall).
              const {
                _affinity_reveal: _stripped,
                _score_reveal: _scoreStripped,
                ...rest
              } = raw
              void _stripped
              void _scoreStripped
              const parsed = rest as unknown as PlayerView
              if (
                typeof parsed === 'object' &&
                parsed !== null &&
                (parsed.your_slot === 0 || parsed.your_slot === 1)
              ) {
                this._cb.onSnapshot(parsed)
              }
            } catch {
              // Bad JSON in TURN_APPLIED — surface as protocol error.
              this._cb.onError(
                new ProtocolError('TURN_APPLIED your_state_json parse failure'),
              )
            }
          }
          // Plan 14-08 (MUI-09) — same envelope-level status surface as
          // STATE_SNAPSHOT. Terminal transitions arrive via TURN_APPLIED.
          const statusTurn = env.turnApplied?.yourState?.status
          if (typeof statusTurn === 'string' && this._cb.onStatus) {
            this._cb.onStatus(statusTurn)
          }
          break
        }
        case runegambit.v1.MessageKind.PING: {
          // Reply PONG with same ts. Note: `ts` lives on the Ping/Pong inner
          // messages (not on the Envelope itself — Envelope has no `ts`).
          const pingTs = env.ping?.ts ?? 0
          const pongFrame = encodeFrame({
            kind: runegambit.v1.MessageKind.PONG,
            pong: runegambit.v1.Pong.create({ ts: pingTs }),
          })
          this._sendBytes(pongFrame)
          break
        }
        case runegambit.v1.MessageKind.PONG: {
          // No-op — server-driven PONG (rare; we don't currently send PINGs).
          break
        }
        case runegambit.v1.MessageKind.ERROR: {
          const code = env.error?.code ?? 'UNKNOWN'
          const msg = env.error?.message ?? 'server error'
          this._cb.onError(new Error(`server error: ${code}: ${msg}`))
          break
        }
        case runegambit.v1.MessageKind.EMOTE_RECEIVED: {
          // Plan 20-07 — META-08. Dispatch to registered overlay callbacks.
          // T-20-47 mitigation: validate field shapes; malformed frames
          // silently drop (no throw, no callback fire).
          const er = env.emoteReceived
          if (
            er &&
            typeof er.emoteId === 'string' &&
            er.emoteId.length > 0 &&
            typeof er.senderSlot === 'number' &&
            (er.senderSlot === 0 || er.senderSlot === 1) &&
            er.sentAt !== null &&
            er.sentAt !== undefined
          ) {
            // protobuf uint64 may arrive as Long; normalize to number.
            const sentAtNum =
              typeof er.sentAt === 'number' ? er.sentAt : Number(er.sentAt)
            if (Number.isFinite(sentAtNum)) {
              const ev: EmoteReceivedEvent = {
                emoteId: er.emoteId,
                senderSlot: er.senderSlot as 0 | 1,
                sentAt: sentAtNum,
              }
              for (const cb of emoteReceivedCallbacks) {
                try {
                  cb(ev)
                } catch {
                  // Listener error must not poison the dispatch loop.
                }
              }
            }
          }
          break
        }
        default:
          // Unknown kind — defensive ignore.
          break
      }
    } catch (e) {
      if (e instanceof SchemaVersionError) {
        // Wire-version mismatch — terminate, do not reconnect.
        this._cb.onError(e)
        try {
          this._ws?.close(1008, 'unsupported schema')
        } catch {
          // ignore
        }
      } else if (e instanceof ProtocolError) {
        this._cb.onError(e)
        try {
          this._ws?.close(1003, 'protocol error')
        } catch {
          // ignore
        }
      } else if (e instanceof B12FirewallViolation) {
        this._cb.onError(e)
        try {
          this._ws?.close(4007, 'firewall violation')
        } catch {
          // ignore
        }
      } else {
        this._cb.onError(e instanceof Error ? e : new Error(String(e)))
      }
    }
  }

  private _handleClose(e: CloseEvent): void {
    const code = e.code

    // Clean app-driven close → terminate.
    if (code === 1000 || this._state === 'terminated') {
      // MUI-08 (Plan 14-07): graceful natural-end → set _closed so any
      // already-scheduled reconnect timer short-circuits before re-opening.
      this._closed = true
      this._state = 'terminated'
      return
    }

    // Protocol-fatal codes — do not reconnect.
    if (code === 1003 || code === 1008) {
      // MUI-08 (Plan 14-07): protocol-fatal natural-end → set _closed so a
      // stale reconnect timer cannot spawn a new socket against a server
      // that has already rejected our wire version / payload shape.
      this._closed = true
      this._cb.onError(new Error(`ws closed with protocol-fatal code ${code}`))
      this._state = 'terminated'
      return
    }

    // Already in fallback or terminated → no further action.
    if (this._state === 'fallback') return

    // Schedule reconnect or fall back.
    if (this._reconnectAttempts >= MAX_RECONNECTS) {
      this._state = 'fallback'
      this._cb.onFallback()
      // The WS client now sits in 'fallback' state — Plan 05's useMatchData
      // stops the WS path and starts polling on this signal. The caller
      // calls `close()` when unmounting; until then the FSM stays parked
      // here so the state property is observable.
      return
    }

    const delay = backoffMs(this._reconnectAttempts)
    this._reconnectAttempts += 1
    this._state = 'reconnecting'
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null
      // MUI-08 (Plan 14-07): race guard — if close() (or a natural-end
      // _handleClose branch) ran between schedule and fire, the _closed
      // flag is true and we MUST NOT resurrect the socket. The state
      // assignment below would otherwise overwrite 'terminated' and the
      // subsequent open() would construct a fresh WebSocket on an
      // explicitly-closed client.
      if (this._closed) return
      // Re-open a fresh socket. open() will set state back to 'connecting'.
      this._state = 'disconnected' // allow open() to proceed
      // Plan 21-06: open() is now async — fire-and-forget. open()'s own
      // body already routes errors through onError + transitions to
      // 'terminated', so this .catch is defensive (should never fire).
      void this.open().catch((err) => {
        // Defensive: should not happen — open() catches its own errors.
        // eslint-disable-next-line no-console
        console.error('[ws-client] reconnect open() unexpectedly threw', err)
      })
    }, delay)
  }
}

// Re-export the SCHEMA_VERSION constant for callers that need it.
export { SCHEMA_VERSION }
